/**
 * <lc-slider> — initialises a Swiper instance from data-slider-config (lc-slider block, lc-header-announcement-slider section).
 *
 * The init logic lives in a custom element so it re-runs whenever the Theme Editor re-renders the block.
 * Autoplay is timer-based instead of Swiper's module, whose permanent rAF loop keeps the main thread busy.
 */
class Slider extends HTMLElement {
  /** @type {any} Swiper ships without type definitions. */
  #swiper;
  #autoplayDelay = 0;
  /** @type {number | undefined} */
  #autoplayTimer;
  #isReady = false;
  #isVisible = false;
  #isHovered = false;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    const { Swiper } = /** @type {any} */ (window);

    // Swiper may load later (e.g. delayed by Cookiebot); setTimeout keeps retrying in hidden tabs.
    if (typeof Swiper === "undefined") {
      setTimeout(() => this.isConnected && this.connectedCallback(), 50);
      return;
    }

    if (this.#swiper) return;

    const { autoplay, ...config } = this.#readConfig();

    if (config.pagination) {
      config.pagination = {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      };
    }

    if (config.navigation) {
      config.navigation = {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      };
    }

    // Swipes on the scrollable fallback leave a native scroll offset that Swiper doesn't reset.
    this.scrollLeft = 0;

    this.#swiper = new Swiper(this, config);

    if (autoplay) this.#setupAutoplay(autoplay);
  }

  disconnectedCallback() {
    clearTimeout(this.#autoplayTimer);
    this.#observer?.disconnect();
    this.#abortController?.abort();
    this.#swiper?.destroy(true, true);
    this.#swiper = undefined;
  }

  /** Starts autoplay after page load and pauses it while hidden, off-screen or hovered. */
  #setupAutoplay({ delay = 3000, pauseOnMouseEnter = false }) {
    this.#autoplayDelay = delay;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Without Swiper's autoplay the a11y module would announce every automatic slide change.
    this.#swiper.wrapperEl.setAttribute("aria-live", "off");

    // Restarts the countdown after automatic and manual slide changes alike.
    this.#swiper.on("slideChange", () => this.#scheduleNext());
    // Resumes autoplay once a breakpoint change leaves more slides than fit into view.
    this.#swiper.on("lock unlock", () => this.#scheduleNext());
    document.addEventListener("visibilitychange", () => this.#scheduleNext(), { signal });

    if (pauseOnMouseEnter) {
      this.addEventListener("mouseenter", () => this.#setHovered(true), { signal });
      this.addEventListener("mouseleave", () => this.#setHovered(false), { signal });
    }

    this.#observer = new IntersectionObserver((entries) => {
      this.#isVisible = entries.some((entry) => entry.isIntersecting);
      this.#scheduleNext();
    });
    this.#observer.observe(this);

    const start = () => {
      this.#isReady = true;
      this.#scheduleNext();
    };

    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true, signal });
  }

  /** @param {boolean} isHovered */
  #setHovered(isHovered) {
    this.#isHovered = isHovered;
    this.#scheduleNext();
  }

  #scheduleNext() {
    clearTimeout(this.#autoplayTimer);

    // A locked Swiper shows all slides; sliding would only retrigger its loop warning.
    if (!this.#isReady || !this.#isVisible || this.#isHovered || document.hidden || this.#swiper.isLocked) return;

    // Matches Swiper's autoplay, which counts the delay from the end of the transition.
    const timeout = this.#autoplayDelay + this.#swiper.params.speed;
    this.#autoplayTimer = setTimeout(() => this.#slideNext(), timeout);
  }

  #slideNext() {
    const swiper = this.#swiper;
    if (!swiper || swiper.destroyed) return;

    if (swiper.isEnd && !swiper.params.loop && !swiper.params.rewind) {
      swiper.slideTo(0);
    } else {
      swiper.slideNext();
    }
  }

  #readConfig() {
    try {
      return JSON.parse(this.dataset.sliderConfig ?? "{}");
    } catch (error) {
      console.warn("[lc-slider] invalid config", error);
      return {};
    }
  }
}

if (!customElements.get("lc-slider")) {
  customElements.define("lc-slider", Slider);
}
